import { HttpService } from '../../../core/http/http.service';
import { Injectable } from '@angular/core';
import { QuestionnaireResponseAPIObject, createQuestionaryForApi } from '../model/questionnaire-response-api-object.model';
import { QuestionaryApiVersion, QuestionaryCore, QuestionaryNames, QuestionnaireResponse, QuestionnaireResponseFromApi, QuestionariType } from '../model/questionnaire-response';
import { Questionnaire } from '../model/questionaries.model';
import { parseQuestionaryDate, parseQuestionnariesFromApi } from './questionnaires-helpers';
import { PatientInfoAPI } from '../medical-record-page/pages/patient-informations/model/patient.model';
import { PatientService } from 'src/app/shared/services/patient/patient-info.service';
import { IUser, UserPermission } from 'src/app/core/models/user.model';
import { MedicalRecordState } from '../store/medical-record.state';
import { Store } from '@ngrx/store';
import { firstValueFrom, } from 'rxjs';
import { getAuthStateSelect } from 'src/app/core/store/auth/auth.selector';
import { LoadingService } from 'src/app/shared/components/loading/loading.service';
import { typeNameToTabNameMap } from 'src/app/shared/components/tabs/tabs';
import { addQuestionnaire, updateQuestionnaire } from '../store/medical-record.action';
import { Just, Nothing } from 'purify-ts';
import _ from 'lodash';


type ApiResponse<T extends QuestionaryCore> = QuestionnaireResponseFromApi<QuestionaryApiVersion<T>>

@Injectable({
  providedIn: 'root'
})
export class QuestionnairesService {

  patient: PatientInfoAPI | null = null;

  constructor(
    private httpService: HttpService,
    private loadingService: LoadingService,
    private store: Store<MedicalRecordState>,
    private patientInfoService: PatientService
  ) {
    this.patientInfoService.selectedPatientObs.subscribe(patient => {
      this.patient = patient;
    })
  }

  async checkIfCanEditMedicalRecord(patientId: string, practitionerId: string) {

    const apiResult = await this.httpService.req<{ permissions: UserPermission, practitionerEditing?: { practitionerId: string, practitionerName: string }[] }>('newProntuario', 'questionnaire', {
      apiVersion: 2,
      loaderKey: 'footer',
      params: {
        patientId,
        practitionerId,
        lg: 'pt-BR'
      },
      method: 'get'
    });

    if (apiResult.status === 'notok') {
      throw new Error(apiResult.internalStatusCode)
    }

    return apiResult.result;
  }

  async cancelAllDraftRecords(patientId: string, practitionerId: string) {


    const apiResult = await this.httpService.req('cancelProntuario', 'questionnaire', {
      apiVersion: 2,
      loaderKey: 'footer',
      // loaderKey:
      params: {
        patientId,
        practitionerId,
        lg: 'pt-BR'
      },
      method: 'post'
    });

    if (apiResult.status === 'notok') {
      throw new Error(apiResult.internalStatusCode)
    }

    return apiResult.result;
  }

  async getRadioAndSurgery<T extends QuestionaryCore>(questionnaireName: string, patientId: string, practitionerId: string) {
    try {
      const apiResult = await this.httpService.req<any>('radioAndSurgery', 'questionnaire', {
        apiVersion: 2,
        params: {
          questionnaireName,
          patientId,
          practitionerId,
          lg: 'pt-BR'
        },
        method: 'get'
      });

      if (apiResult.status === 'notok') {
        throw new Error(apiResult.internalStatusCode)
      }
      const { result } = apiResult;
      const r = await parseQuestionnariesFromApi(result);

      return r

    } catch (error) {

      throw error
    }
  }

  async getQuestionnaires<T extends QuestionaryCore>(questionnaireName: any, patientId: string, userId: string | null): Promise<QuestionnaireResponse<T>> {

    const user = await firstValueFrom(this.store.select(getAuthStateSelect));
    const practitionerId = userId ?? user.user?.practitionerId!;

    try {
      const apiResult = await this.httpService.req<ApiResponse<T>>('prontuario', 'questionnaire', {
        apiVersion: 2,
        params: {
          questionnaireName,
          patientId,
          practitionerId,
          lg: 'pt-BR'
        },
        method: 'get'
      });

      if (apiResult.status === 'notok') {
        throw new Error(apiResult.internalStatusCode);
      }

      const { result } = apiResult;
      const questionnaireResponses = parseQuestionnariesFromApi(result);

      return questionnaireResponses;

    } catch (error) {
      throw error
    }
  }

  /**
   * Cria questionario NOVO (data de questionario/criacao fica como agora)
   */
  private createNewQuestionayCoreFromQuestions(item: Object, user: IUser): QuestionaryCore {
    return {
      ...item,
      practitionerName: user.name,
      questionnaireDate: new Date(),
      questionnaireName: '',
      questionnaireResponseId: '',
      practitionerSpecialty: user.userProfile
    }
  }

  async saveNewQuestionnaireDraft_forSignalStore({ item, name }: { item: Object, name: string }) {
    try {
      const user = await firstValueFrom(this.store.select(getAuthStateSelect))
      if (user.user == null) {
        console.error(user.user)
        throw new Error('user == null !!!!')
      }
      const question = this.createNewQuestionayCoreFromQuestions(item, user.user!)
      const toSave = createQuestionaryForApi({ patientId: this.patient?.patientId!, question, userId: user.user?.practitionerId!, diagnosisId: this.patient?.patientMedicalRecord?.diagnosisId! })
      const newQuest = await this.postDraftQuestionnaire(name as QuestionaryNames, toSave)
      return Just(newQuest)
    } catch (error) {
      console.error(error)
      return Nothing
    }
  }
  async updateQuestionarie_forSignalStore({ newQuestion, name, original }: { newQuestion: Object, name: QuestionaryNames, original: QuestionaryCore }) {
    // console.log({item})
    try {
      const user = await firstValueFrom(this.store.select(getAuthStateSelect))
      const question = {
        ...original,
        ...newQuestion
      }

      const toSave = createQuestionaryForApi({
        patientId: this.patient?.patientId!,
        question, userId: user.user?.practitionerId!,
        diagnosisId: this.patient?.patientMedicalRecord?.diagnosisId!
      }
      )
      const result = await this.putDraftQuestionnaire(name, toSave)
      if (result.status == 'notok') {
        return Nothing
      }
      return Just(result.result)
    } catch (error) {
      console.error(error)
      return Nothing
    }
  }


  // todo: usar descrimination de union type ex: {name: 'toxicity' , item : ToxicityQuestionnaireResponseHistory } | ....
  async saveNewQuestionnaireDraft({ item, name }: QuestionariType) {
    const user = await firstValueFrom(this.store.select(getAuthStateSelect))
    const toSave = createQuestionaryForApi({ patientId: this.patient?.patientId!, question: item, userId: user.user?.practitionerId!, diagnosisId: this.patient?.patientMedicalRecord?.diagnosisId! })
    const newQuest = await this.postDraftQuestionnaire(name, toSave)
    this.store.dispatch(addQuestionnaire({ name, item: newQuest } as QuestionariType))
    return newQuest;
  }

  async updateQuestionnaireDraft({ item, name }: QuestionariType) {
    try {
      const user = await firstValueFrom(this.store.select(getAuthStateSelect))
      const toSave = createQuestionaryForApi({ patientId: this.patient?.patientId!, question: item, userId: user.user?.practitionerId!, diagnosisId: this.patient?.patientMedicalRecord?.diagnosisId! })
      const newQuest = await this.putDraftQuestionnaire(name, toSave).then(res => {
        if (res.status == 'notok') {
          throw new Error();
        }
        return parseQuestionaryDate(res.result)
      });
      this.store.dispatch(updateQuestionnaire({ name, item: newQuest } as QuestionariType))
      return newQuest;
    } catch (error) {
      throw new Error();
    }
  }

  async postDraftQuestionnaire<T extends Questionnaire | QuestionaryCore>(questionnaireName: QuestionaryNames, questionnaire: QuestionnaireResponseAPIObject<T>) {
    const response = await this.httpService.req<QuestionaryApiVersion<T>>('writeQuestionnaireResponse', 'questionnaire', {
      apiVersion: 2,
      loaderKey: `post-${typeNameToTabNameMap[questionnaireName]}`,
      params: {
        questionnaireName,
        lg: 'pt-BR'
      },
      body: questionnaire,
      method: 'post'
    });
    if (response.status !== 'ok') {
      throw new Error(response.internalStatusCode)
    }
    return parseQuestionaryDate(response.result)
  }

  putDraftQuestionnaire<T extends QuestionaryCore>(questionnaireName: QuestionaryNames, questionnaire: QuestionnaireResponseAPIObject<T>) {

    return this.httpService.req<T>('updateQuestionnaireResponse', 'questionnaire', {
      apiVersion: 2,
      loaderKey: `put-${typeNameToTabNameMap[questionnaireName]}`,
      params: {
        questionnaireName,
        lg: 'pt-BR'
      },
      body: questionnaire,
      method: 'put'
    });
  }

  async confirmQuestionnaireDrafts<T extends QuestionaryCore>(patientId: string, practitionerId: string) {
    try {
      const response = await this.httpService.req<ApiResponse<T>>('saveProntuario', 'questionnaire', {
        apiVersion: 2,
        loaderKey: 'footer',
        params: {
          patientId,
          practitionerId,
          lg: 'pt-BR'
        },
        method: 'post'
      });

      if (response.status == 'ok') {
        return { response, status: 'newInfo' as const }
      }
      return {
        response, status: 'error' as const
      }

    } catch (error) {
      console.error({ error })

      if (error?.error?.internalStatusCode == '802') {
        return {
          error, status: 'noNewInfo' as const
        }
      }
      return { error, status: 'error' as const }

    }
  }

  async deleteQuestionnaire({ idToDelete, name }: { idToDelete: string, name: QuestionariType['name'] }) {
    try {
      const response = await this.fetchDeleteQuestionnaire(name, idToDelete);
      if (response.status !== 'ok') {
        throw response.internalStatusCode
      }
      // this.store.dispatch()
      return response
    } catch (error) {
      throw error
    }

  }

  fetchDeleteQuestionnaire<T extends QuestionaryCore>(questionnaireName: QuestionaryNames, questionnaireResponseId: string) {

    return this.httpService.req<ApiResponse<T>>('deleteQuestionnaireResponse', 'questionnaire', {
      apiVersion: 2,
      loaderKey: `delete-${typeNameToTabNameMap[questionnaireName]}`,
      params: {
        questionnaireName,
        questionnaireResponseId,
        lg: 'pt-BR'
      },
      method: 'delete'
    });
  }
}
